import { IAPIRoutes } from "../../../../config";

export type IEhrRecordsRepository = ReturnType<
  typeof EhrRecordsRepositoryFactory
>;

export const EhrRecordsRepositoryFactory = ({
  APIProvider,
  AuthService,
  APIRoutes,
}: {
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  APIRoutes: IAPIRoutes;
}) => {
  const { GET, POST } = APIProvider;
  const { getAuthenticationToken } = AuthService;

  const getEhrMedicationsAPI: IGetEhrMedicationsAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrMedications
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.status) {
      params.status = payload.status;
    }
    if (typeof payload.limit === "number") {
      params.limit = payload.limit.toString();
    }
    if (typeof payload.offset === "number") {
      params.offset = payload.offset.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getEhrNutritionAPI: IGetEhrNutritionAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrNutrition
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.status) {
      params.status = payload.status;
    }
    if (typeof payload.limit === "number") {
      params.limit = payload.limit.toString();
    }
    if (typeof payload.offset === "number") {
      params.offset = payload.offset.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getEhrAssessmentsAPI: IGetEhrAssessmentsAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrAssessments
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.assessment_status) {
      params.assessment_status = payload.assessment_status;
    }
    if (payload.assessment_type) {
      params.assessment_type = payload.assessment_type;
    }
    if (typeof payload.limit === "number") {
      params.limit = payload.limit.toString();
    }
    if (typeof payload.offset === "number") {
      params.offset = payload.offset.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getEhrAllergiesAPI: IGetEhrAllergiesAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrAllergies
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (typeof payload.status === "number") {
      params.status = payload.status.toString();
    }
    if (payload.category) {
      params.category = payload.category;
    }
    if (payload.clinical_status) {
      params.clinical_status = payload.clinical_status;
    }
    if (typeof payload.limit === "number") {
      params.limit = payload.limit.toString();
    }
    if (typeof payload.offset === "number") {
      params.offset = payload.offset.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getEhrLabsAPI: IGetEhrLabsAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrLabs
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.report_type) {
      params.report_type = payload.report_type;
    }
    if (payload.report_status) {
      params.report_status = payload.report_status;
    }
    if (typeof payload.limit === "number") {
      params.limit = payload.limit.toString();
    }
    if (typeof payload.offset === "number") {
      params.offset = payload.offset.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPccAssessmentsAPI: IGetPccAssessmentsAPI = async (payload) => {
    const url = APIRoutes.apiGetEhrAssessmentsV2
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.assessment_status) {
      params.assessment_status = payload.assessment_status;
    }
    if (payload.start_date) {
      params.start_date = payload.start_date;
    }
    if (payload.end_date) {
      params.end_date = payload.end_date;
    }
    if (typeof payload.template_id === "number") {
      params.template_id = payload.template_id.toString();
    }
    if (typeof payload.system_template_id === "number") {
      params.system_template_id = payload.system_template_id.toString();
    }
    if (typeof payload.template_version === "number") {
      params.template_version = payload.template_version.toString();
    }
    if (typeof payload.latest_template_version === "boolean") {
      params.latest_template_version =
        payload.latest_template_version.toString();
    }
    if (typeof payload.page === "number") {
      params.page = payload.page.toString();
    }
    if (typeof payload.page_size === "number") {
      params.page_size = payload.page_size.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getPccAssessmentDetailAPI: IGetPccAssessmentDetailAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetEhrAssessmentsByAssessmentID
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{assessment_id}", payload.assessment_id.toString());

    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getCarePlansAPI: IGetCarePlansAPI = async (payload) => {
    const url = APIRoutes.apiGetCarePlan
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const params: Record<string, string> = {};
    if (payload.care_plan_status) {
      params.care_plan_status = payload.care_plan_status;
    }
    if (payload.focus_status) {
      params.focus_status = payload.focus_status;
    }
    if (typeof payload.page === "number") {
      params.page = payload.page.toString();
    }
    if (typeof payload.page_size === "number") {
      params.page_size = payload.page_size.toString();
    }

    return GET(url, params, {
      Authorization: `Bearer ${await getAuthenticationToken()}`,
    });
  };

  const getCarePlanFocusesAPI: IGetCarePlanFocusesAPI = async (payload) => {
    const url = APIRoutes.apiGetCarePlanFocusesByFocusIds
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    return GET(
      url,
      { focus_ids: payload.focus_ids },
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const getPccAvailableNoteTypesAPI: IGetPccAvailableNoteTypesAPI = async (
    payload
  ) => {
    const url = APIRoutes.apiGetPccAvailableNoteTypes.replace(
      "{clinic_id}",
      payload.clinic_id.toString()
    );

    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const downloadEhrLabAPI: IDownloadEhrLabAPI = async (payload) => {
    const url = APIRoutes.apiDownloadEHRLabs
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString())
      .replace("{lab_id}", payload.lab_id.toString());

    return GET(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );
  };

  const requestPccReconcileAPI: IRequestPccReconcileAPI = async (payload) => {
    const url = APIRoutes.apiRequestPccReconcile
      .replace("{clinic_id}", payload.clinic_id.toString())
      .replace("{patient_id}", payload.patient_id.toString());

    const response = await POST(
      url,
      {},
      {
        Authorization: `Bearer ${await getAuthenticationToken()}`,
      }
    );

    // The API provider returns the error response body (rather than throwing)
    // for non-2xx responses, so a successful reconcile is identified by the
    // presence of `message_id`. Anything else (e.g. a 503 when Kafka is
    // unreachable, shaped as `{ detail: { message } }`) is surfaced as a
    // failure so the service reports `success: false`.
    if (!response || !response.message_id) {
      throw new Error(
        response?.detail?.message ||
          response?.message ||
          "Could not request a PointClickCare resync."
      );
    }

    return response;
  };

  return {
    getEhrMedicationsAPI,
    getEhrNutritionAPI,
    getEhrAssessmentsAPI,
    getEhrAllergiesAPI,
    getEhrLabsAPI,
    downloadEhrLabAPI,
    getPccAssessmentsAPI,
    getPccAssessmentDetailAPI,
    getCarePlansAPI,
    getCarePlanFocusesAPI,
    getPccAvailableNoteTypesAPI,
    requestPccReconcileAPI,
  };
};
